import { mapIssueToSavedIssue } from '@deque/axe-devtools-issues-utils';
import { Button, Link, Loader } from '@deque/cauldron-react';
import type { GeneralSettingsSchema } from '@deque/orgwide-settings-client';
import {
  ConfigurationContext,
  IssuePage,
  type IssueType
} from '@deque/ui-issues';
import '@deque/ui-issues/dist/styles/index.css';
import React, { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PageTitle from '~/common/components/PageTitle';
import { ScreenshotStatus } from '~/common/constants';
import { useAxeDevtoolsProAnalytics } from '~/common/contexts/analytics';
import { useAuthContext } from '~/common/contexts/auth';
import { useConfiguration } from '~/common/contexts/Configuration';
import { useScreenshot } from '~/common/contexts/Screenshot';
import { useServerInfo } from '~/common/contexts/serverInfo';
import { isNotFoundError } from '~/common/utils/errors';
import ErrorMessage from '../components/issue/ErrorMessage';
import useIssue from '../hooks/useIssue';
import useScreenshotConfiguration from '../hooks/useScreenshotConfigurationState';
import styles from './Issue.css';

interface RouteParams {
  id: string;
}

interface Props {
  className?: string;
  loadIssueData?: typeof useIssue;
}

const IssueV2 = ({ loadIssueData = useIssue }: Props) => {
  const { t } = useTranslation();
  const { id } = useParams<RouteParams>();
  const loaderRef = useRef<HTMLDivElement>(null);
  const { user, login } = useAuthContext();
  const { settings, loading: configurationLoading } = useConfiguration();
  const { serverInfo } = useServerInfo();
  const {
    issue,
    loading: issueLoading,
    error: issueError
  } = loadIssueData(id, user?.token);

  const {
    screenshot,
    loading: screenshotLoading,
    error: screenshotError
  } = useScreenshot(issue?.screenshot_id as string);

  const mappedIssue = issue
    ? mapIssueToSavedIssue<IssueType>(issue, {
        help: issue?.help,
        ruleId: issue?.rule,
        integrations: issue?.integrationUrl
          ? [{ type: 'jira', url: issue?.integrationUrl }]
          : []
      })
    : null;

  const screenshotConfiguration = useScreenshotConfiguration(
    settings?.general ?? null,
    (issue?.general_settings as GeneralSettingsSchema) ?? null
  );

  const analytics = useAxeDevtoolsProAnalytics();

  const handleLogin = (): void => {
    login();
  };

  const screenshotStatus: ScreenshotStatus = screenshotLoading
    ? ScreenshotStatus.Loading
    : screenshotError
      ? ScreenshotStatus.Error
      : ScreenshotStatus.Complete;

  useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  useEffect(() => {
    if (mappedIssue) {
      analytics.issueViewSharedIssue({
        ruleName: mappedIssue.ruleId
      });
    }
  }, [mappedIssue, analytics]);

  if (issueLoading || configurationLoading || screenshotLoading) {
    return (
      <div className={styles.page}>
        <PageTitle title={t('Loading issue data')} />
        <Loader label={t('Loading issue data')} tabIndex={-1} ref={loaderRef} />
      </div>
    );
  }

  if (!issue || isNotFoundError(issueError as Error)) {
    const errorMessage = user ? (
      t('You may need to ensure you have access to this shared issue.')
    ) : (
      <Trans>
        You may need to{' '}
        <Button onClick={handleLogin} variant="link">
          sign in
        </Button>{' '}
        or ensure you have access to this shared issue.
      </Trans>
    );

    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <PageTitle title={t('Shared issue not found')} />
          <ErrorMessage
            error={
              <>
                <Trans>We were unable to find this issue.</Trans> {errorMessage}
              </>
            }
            title={t('Shared issue not found')}
          />
        </div>
      </div>
    );
  }

  if (issueError || !issue) {
    return (
      <>
        <PageTitle title={t('Failed to load shared issue')} />
        <ErrorMessage
          error={
            <>
              <Trans>We were unable to load this issue.</Trans>{' '}
              <Trans>
                Please try again, and if the issue persists, contact{' '}
                <Link href="mailto:helpdesk@deque.com">helpdesk@deque.com</Link>
                .
              </Trans>
            </>
          }
          title={t('Failed to load shared issue')}
        />
      </>
    );
  }

  const screenshotURL = screenshot
    ? URL.createObjectURL(screenshot?.blob as Blob)
    : null;

  return (
    <ConfigurationContext.Provider
      value={{
        screenshots: !!serverInfo?.screenshotsEnabled,
        integrations: false
      }}
    >
      <IssuePage
        issue={{
          ...mappedIssue,
          screenshotURL
        }}
        //Temporarely pass empty <span>, because header expecting this prop to be passed.
        // Comment referrence: https://github.com/dequelabs/walnut/pull/9837#discussion_r1828274832
        leadingHeaderActions={<span />}
        screenshotStatus={screenshotStatus}
        screenshotConfiguration={screenshotConfiguration}
      />
    </ConfigurationContext.Provider>
  );
};

export default IssueV2;
